import { Box, Button, chakra, Flex } from "@chakra-ui/react";
import { LocalDateTime } from "@js-joda/core";
import React from "react";
import BaseDatePicker, { BaseDatePickerProps } from "./BaseDatePicker";

const CalendarBox = chakra(Box, {
  baseStyle: {
    display: "flex",
    width: "fit-content",
    gap: 3,
  },
});

const ActionsBox = chakra(Box, {
  baseStyle: {
    borderColor: "gray.200",
    borderTopWidth: "1px",
    paddingTop: 2,
    marginTop: 2,
  },
});

export interface SingleDateTimePickerProps
  extends Omit<BaseDatePickerProps, "monthsShown" | "onChange"> {
  onChange: (
    date: LocalDateTime | null,
    event: React.SyntheticEvent<unknown, Event> | undefined
  ) => void;
}

const PropsContext = React.createContext<SingleDateTimePickerProps | null>(null);

// Declared here rather than inline - see the comment in RangeDatePicker.
function CalendarContainer({ children }: { children: React.ReactNode[] }) {
  const datePicker = React.useContext(PropsContext);

  if (datePicker === null) {
    return null;
  }

  return (
    <>
      <CalendarBox>{children}</CalendarBox>
      <ActionsBox>
        <Flex alignItems="center" gap={8} justifyContent="space-between">
          <Button
            color="negative"
            size="sm"
            variant="ghost"
            onClick={(e) => datePicker.onChange(null, e)}
          >
            Clear
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => datePicker.onChange(LocalDateTime.now(), e)}
          >
            Today
          </Button>
        </Flex>
      </ActionsBox>
    </>
  );
}

const SingleDateTimePicker = (props: SingleDateTimePickerProps) => {
  return (
    <PropsContext.Provider value={props}>
      <BaseDatePicker
        {...props}
        showTimeSelect
        calendarContainer={CalendarContainer}
        dateFormat="MM/dd/yyyy HH:mm"
        monthsShown={1}
        shouldCloseOnSelect={false}
        timeFormat="HH:mm"
        timeIntervals={15}
      />
    </PropsContext.Provider>
  );
};

export default SingleDateTimePicker;
